import React, { useState } from "react";
import {
  createJsa,
  updateJsa,
  lockJsa,
  exportJsaPdf,
} from "../../services/jsaService";
import { useTranslation } from "react-i18next";
import SignatureBlock from "./SignatureBlock";
import { JOB_TYPES as JOB_TYPE_KEYS, RISK_LEVELS, jobTypeLabel, riskLabel } from "../../utils/jsaLabels";

// Values are saved in English; labels are translated (utils/jsaLabels).
const JOB_TYPES = Object.keys(JOB_TYPE_KEYS);

const RISK_COLORS = {
  Low: "#00cc66",
  Medium: "#ffaa00",
  High: "#ff6600",
  Critical: "#ff4466",
};

const EMPTY_STEP = {
  step: "",
  hazards: "",
  controls: "",
  riskLevel: "",
  responsibleParty: "",
};

const cyan = "#00f5ff";
const border = "rgba(0,245,255,0.15)";
const card = "rgba(0,245,255,0.03)";

const inputStyle = {
  width: "100%",
  backgroundColor: "rgba(0,245,255,0.05)",
  border: "1px solid rgba(0,245,255,0.2)",
  borderRadius: "6px",
  padding: "8px 12px",
  color: "white",
  fontSize: "13px",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "'Segoe UI', sans-serif",
};

const labelStyle = {
  color: cyan,
  fontSize: "10px",
  letterSpacing: "2px",
  textTransform: "uppercase",
  display: "block",
  marginBottom: "4px",
};

export default function JsaModal({
  wellId,
  jsa,
  mode,
  token,
  onClose,
  onSaved,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(
    jsa || {
      format: "api",
      date: new Date().toISOString().split("T")[0],
      jobType: "",
      location: "",
      preparedBy: "",
      reviewedBy: "",
      permitRequired: false,
      h2sPresent: false,
      h2sConcentration: "",
      weatherConditions: "",
      steps: [{ ...EMPTY_STEP }],
      notes: "",
      signatures: [],
    },
  );

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const readOnly = mode === "view" || jsa?.locked;

  function updateField(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function addStep() {
    setForm((f) => ({ ...f, steps: [...f.steps, { ...EMPTY_STEP }] }));
  }

  function removeStep(idx) {
    setForm((f) => ({ ...f, steps: f.steps.filter((_, i) => i !== idx) }));
  }

  function updateStep(idx, field, value) {
    setForm((f) => {
      const steps = [...f.steps];
      steps[idx] = { ...steps[idx], [field]: value };
      return { ...f, steps };
    });
  }

  const save = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const today = new Date().toISOString().split("T")[0];
      const payload = { ...form, date: form.date || today };
      const saved = jsa
        ? await updateJsa(jsa._id, payload, token)
        : await createJsa(wellId, payload, token);
      onSaved(saved);
      onClose();
    } catch (err) {
      console.error("JSA save error:", err);
      setSaveError(
        err.response?.data?.error || t("jsa.saveFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  const lock = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const locked = await lockJsa(jsa._id, token);
      onSaved(locked);
      onClose();
    } catch (err) {
      console.error("JSA lock error:", err);
      setSaveError(err.response?.data?.error || t("jsa.lockFailed"));
    } finally {
      setSaving(false);
    }
  };

  const exportPdf = () => {
    if (jsa?._id) exportJsaPdf(jsa._id, token);
  };

  return (
    <div className="modal-backdrop">
      <div
        className="modal-content"
        style={{
          width: "760px",
          maxWidth: "96vw",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: "800",
                color: "white",
                letterSpacing: "2px",
              }}
            >
              {t("jsa.title")}
            </div>
            <div
              style={{
                fontSize: "10px",
                color: "rgba(0,245,255,0.5)",
                letterSpacing: "2px",
                marginTop: "2px",
              }}
            >
              {t("jsa.compliance")}
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            {jsa?._id && (
              <button
                onClick={exportPdf}
                style={{
                  background: "none",
                  border: `1px solid ${border}`,
                  color: "rgba(0,245,255,0.6)",
                  fontSize: "11px",
                  padding: "5px 12px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  letterSpacing: "1px",
                }}
              >
                {t("jsa.pdf")}
              </button>
            )}
            <button
              onClick={onClose}
              aria-label={t("jsa.closeAria")}
              style={{
                background: "none",
                border: `1px solid ${border}`,
                color: "rgba(0,245,255,0.5)",
                fontSize: "11px",
                padding: "5px 12px",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* FORMAT TOGGLE */}
        {!readOnly && (
          <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
            {["api", "osha"].map((f) => (
              <button
                key={f}
                onClick={() => updateField("format", f)}
                style={{
                  flex: 1,
                  padding: "8px",
                  background:
                    form.format === f
                      ? "linear-gradient(135deg, #00f5ff, #0080ff)"
                      : "none",
                  color: form.format === f ? "#020817" : "rgba(0,245,255,0.5)",
                  border: `1px solid ${border}`,
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "11px",
                  fontWeight: "700",
                  letterSpacing: "2px",
                }}
              >
                {f === "api" ? t("jsa.formatApi") : t("jsa.formatOsha")}
              </button>
            ))}
          </div>
        )}

        {/* SECTION 1 — JOB INFORMATION */}
        <div
          style={{
            backgroundColor: card,
            border: `1px solid ${border}`,
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              fontWeight: "700",
              color: cyan,
              letterSpacing: "3px",
              marginBottom: "14px",
            }}
          >
            {t("jsa.section1")}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
            }}
          >
            <div>
              <label style={labelStyle} htmlFor="jsa-job-type">{t("jsa.jobTypeLabel")}</label>
              <select
                id="jsa-job-type"
                name="jobType"
                value={form.jobType}
                onChange={(e) => updateField("jobType", e.target.value)}
                disabled={readOnly}
                style={{ ...inputStyle, backgroundColor: "#020817" }}
              >
                <option value="">{t("jsa.selectJobType")}</option>
                {JOB_TYPES.map((j) => (
                  <option key={j} value={j}>
                    {jobTypeLabel(t, j)}
                  </option>
                ))}
                {form.jobType && !JOB_TYPES.includes(form.jobType) && (
                  <option value={form.jobType}>{form.jobType}</option>
                )}
              </select>
            </div>
            <div>
              <label style={labelStyle} htmlFor="jsa-location">{t("jsa.location")}</label>
              <input
                id="jsa-location"
                name="location"
                value={form.location}
                onChange={(e) => updateField("location", e.target.value)}
                disabled={readOnly}
                style={inputStyle}
                placeholder={t("jsa.locationPlaceholder")}
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="jsa-prepared-by">{t("jsa.preparedBy")}</label>
              <input
                id="jsa-prepared-by"
                name="preparedBy"
                value={form.preparedBy}
                onChange={(e) => updateField("preparedBy", e.target.value)}
                disabled={readOnly}
                style={inputStyle}
                placeholder={t("jsa.namePlaceholder")}
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="jsa-reviewed-by">{t("jsa.reviewedBy")}</label>
              <input
                id="jsa-reviewed-by"
                name="reviewedBy"
                value={form.reviewedBy}
                onChange={(e) => updateField("reviewedBy", e.target.value)}
                disabled={readOnly}
                style={inputStyle}
                placeholder={t("jsa.namePlaceholder")}
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="jsa-weather">{t("jsa.weather")}</label>
              <input
                id="jsa-weather"
                name="weatherConditions"
                value={form.weatherConditions}
                onChange={(e) =>
                  updateField("weatherConditions", e.target.value)
                }
                disabled={readOnly}
                style={inputStyle}
                placeholder={t("jsa.weatherPlaceholder")}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "20px",
                alignItems: "flex-end",
                paddingBottom: "4px",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  color: "rgba(255,255,255,0.7)",
                  fontSize: "12px",
                }}
              >
                <input
                  id="jsa-permit"
                  name="permitRequired"
                  type="checkbox"
                  checked={form.permitRequired}
                  onChange={(e) =>
                    updateField("permitRequired", e.target.checked)
                  }
                  disabled={readOnly}
                />
                {t("jsa.permitRequired")}
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  color: "rgba(255,255,255,0.7)",
                  fontSize: "12px",
                }}
              >
                <input
                  id="jsa-h2s"
                  name="h2sPresent"
                  type="checkbox"
                  checked={form.h2sPresent}
                  onChange={(e) => updateField("h2sPresent", e.target.checked)}
                  disabled={readOnly}
                />
                {t("jsa.h2sPresent")}
              </label>
            </div>
            {form.h2sPresent && (
              <div>
                <label style={labelStyle} htmlFor="jsa-h2s-conc">{t("jsa.h2sConcentration")}</label>
                <input
                  id="jsa-h2s-conc"
                  name="h2sConcentration"
                  value={form.h2sConcentration}
                  onChange={(e) =>
                    updateField("h2sConcentration", e.target.value)
                  }
                  disabled={readOnly}
                  style={inputStyle}
                  placeholder={t("jsa.h2sPlaceholder")}
                />
              </div>
            )}
          </div>
        </div>

        {/* SECTION 2 — JOB STEPS */}
        <div
          style={{
            backgroundColor: card,
            border: `1px solid ${border}`,
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "14px",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                fontWeight: "700",
                color: cyan,
                letterSpacing: "3px",
              }}
            >
              {t("jsa.section2")}
            </div>
            {!readOnly && (
              <button
                onClick={addStep}
                style={{
                  background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                  color: "#020817",
                  fontWeight: "700",
                  fontSize: "10px",
                  padding: "5px 14px",
                  borderRadius: "4px",
                  border: "none",
                  cursor: "pointer",
                  letterSpacing: "1px",
                }}
              >
                {t("jsa.addStep")}
              </button>
            )}
          </div>

          {/* Column Headers */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                form.format === "osha"
                  ? "2fr 2fr 2fr 1fr 1.5fr 28px"
                  : "2fr 2fr 2fr 28px",
              gap: "8px",
              marginBottom: "8px",
              padding: "6px 8px",
              backgroundColor: "rgba(0,245,255,0.08)",
              borderRadius: "4px",
            }}
          >
            {[
              t("jsa.colStep"),
              t("jsa.colHazards"),
              t("jsa.colControls"),
              ...(form.format === "osha" ? [t("jsa.colRisk"), t("jsa.colParty")] : []),
              "",
            ].map((h, i) => (
              <div
                key={i}
                style={{
                  color: cyan,
                  fontSize: "9px",
                  letterSpacing: "2px",
                  fontWeight: "700",
                }}
              >
                {h}
              </div>
            ))}
          </div>

          {form.steps.map((s, idx) => (
            <div
              key={idx}
              style={{
                display: "grid",
                gridTemplateColumns:
                  form.format === "osha"
                    ? "2fr 2fr 2fr 1fr 1.5fr 28px"
                    : "2fr 2fr 2fr 28px",
                gap: "8px",
                marginBottom: "8px",
                alignItems: "start",
              }}
            >
              <textarea
                id={`step-${idx}`}
                name={`step-${idx}`}
                value={s.step}
                onChange={(e) => updateStep(idx, "step", e.target.value)}
                disabled={readOnly}
                rows={2}
                placeholder={t("jsa.stepPlaceholder", { n: idx + 1 })}
                style={{ ...inputStyle, resize: "vertical", fontSize: "12px" }}
              />
              <textarea
                id={`hazards-${idx}`}
                name={`hazards-${idx}`}
                value={s.hazards}
                onChange={(e) => updateStep(idx, "hazards", e.target.value)}
                disabled={readOnly}
                rows={2}
                placeholder={t("jsa.hazardsPlaceholder")}
                style={{ ...inputStyle, resize: "vertical", fontSize: "12px" }}
              />
              <textarea
                id={`controls-${idx}`}
                name={`controls-${idx}`}
                value={s.controls}
                onChange={(e) => updateStep(idx, "controls", e.target.value)}
                disabled={readOnly}
                rows={2}
                placeholder={t("jsa.controlsPlaceholder")}
                style={{ ...inputStyle, resize: "vertical", fontSize: "12px" }}
              />
              {form.format === "osha" && (
                <>
                  <select
                    id={`risk-${idx}`}
                    name={`riskLevel-${idx}`}
                    value={s.riskLevel}
                    onChange={(e) =>
                      updateStep(idx, "riskLevel", e.target.value)
                    }
                    disabled={readOnly}
                    style={{
                      ...inputStyle,
                      color: s.riskLevel
                        ? RISK_COLORS[s.riskLevel]
                        : "rgba(255,255,255,0.5)",
                      fontWeight: "700",
                      backgroundColor: "#020817",
                    }}
                  >
                    <option value="">--</option>
                    {RISK_LEVELS.map((r) => (
                      <option
                        key={r}
                        value={r}
                        style={{ color: RISK_COLORS[r] }}
                      >
                        {riskLabel(t, r)}
                      </option>
                    ))}
                  </select>
                  <input
                    id={`party-${idx}`}
                    name={`responsibleParty-${idx}`}
                    value={s.responsibleParty}
                    onChange={(e) =>
                      updateStep(idx, "responsibleParty", e.target.value)
                    }
                    disabled={readOnly}
                    placeholder={t("jsa.partyPlaceholder")}
                    style={{ ...inputStyle, fontSize: "12px" }}
                  />
                </>
              )}
              {!readOnly && (
                <button
                  aria-label={t("jsa.removeStep", { n: idx + 1 })}
                  onClick={() => removeStep(idx)}
                  style={{
                    background: "none",
                    border: "1px solid rgba(255,68,102,0.3)",
                    color: "rgba(255,68,102,0.7)",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "12px",
                    padding: "4px 6px",
                    marginTop: "2px",
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          ))}

          {form.steps.length === 0 && (
            <div
              style={{
                textAlign: "center",
                color: "rgba(0,245,255,0.3)",
                fontSize: "12px",
                padding: "20px",
                letterSpacing: "2px",
              }}
            >
              {t("jsa.noSteps")}
            </div>
          )}
        </div>

        {/* SECTION 3 — NOTES */}
        <div
          style={{
            backgroundColor: card,
            border: `1px solid ${border}`,
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              fontWeight: "700",
              color: cyan,
              letterSpacing: "3px",
              marginBottom: "12px",
            }}
          >
            {t("jsa.section3")}
          </div>
          <textarea
            id="jsa-notes"
            name="notes"
            value={form.notes}
            onChange={(e) => updateField("notes", e.target.value)}
            disabled={readOnly}
            rows={4}
            placeholder={t("jsa.notesPlaceholder")}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>

        {/* SECTION 4 — SIGNATURES */}
        <div
          style={{
            backgroundColor: card,
            border: `1px solid ${border}`,
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              fontWeight: "700",
              color: cyan,
              letterSpacing: "3px",
              marginBottom: "12px",
            }}
          >
            {t("jsa.section4")}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "rgba(255,255,255,0.4)",
              marginBottom: "12px",
              lineHeight: "1.6",
            }}
          >
            {t("jsa.acknowledgment")}
          </div>
          <SignatureBlock
            signatures={form.signatures || []}
            onChange={(sigs) => setForm((f) => ({ ...f, signatures: sigs }))}
            readOnly={readOnly}
          />
        </div>

        {/* ERROR */}
        {saveError && (
          <div
            style={{
              color: "#ff4466",
              fontSize: "12px",
              marginBottom: "12px",
              padding: "8px 12px",
              backgroundColor: "rgba(255,68,102,0.1)",
              border: "1px solid rgba(255,68,102,0.3)",
              borderRadius: "6px",
            }}
          >
            ⚠ {saveError}
          </div>
        )}

        {/* ACTION BUTTONS */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {jsa?.locked ? (
            <span
              style={{
                color: "#ffaa00",
                fontSize: "11px",
                letterSpacing: "2px",
              }}
            >
              {t("jsa.lockedSubmitted")}
            </span>
          ) : (
            <div />
          )}
          <div style={{ display: "flex", gap: "8px" }}>
            {!readOnly && (
              <button
                onClick={save}
                disabled={saving}
                style={{
                  background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                  color: "#020817",
                  fontWeight: "700",
                  fontSize: "11px",
                  letterSpacing: "2px",
                  padding: "10px 20px",
                  borderRadius: "6px",
                  border: "none",
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? t("jsa.saving") : t("jsa.save")}
              </button>
            )}
            {jsa && !jsa.locked && mode !== "create" && (
              <button
                onClick={lock}
                disabled={saving}
                style={{
                  background: "none",
                  border: "1px solid #ffaa00",
                  color: "#ffaa00",
                  fontWeight: "700",
                  fontSize: "11px",
                  letterSpacing: "2px",
                  padding: "10px 20px",
                  borderRadius: "6px",
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? t("jsa.saving") : t("jsa.lockSubmit")}
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: `1px solid ${border}`,
                color: "rgba(0,245,255,0.5)",
                fontSize: "11px",
                padding: "10px 16px",
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              {t("jsa.close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
